<!-- Legacy Layout - Simple dialog like master -->
@if (useLegacyLayout) {
  <div class="dialog-container-legacy">
    <div class="dialog-header-legacy">
      <h1 class="dialog-title-legacy">Generate payapp</h1>
      <button mat-icon-button (click)="onCancelClick()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="dialog-message-legacy">
      {{ data.itemCount }} items are included in the report. Total amount of {{ data.totalAmount | currency:'USD' }} USD.
    </div>
    <form [formGroup]="payappForm">
      <label class="payapp-label-legacy">ADD PAYAPP NAME (OPTIONAL):</label>
      <mat-form-field class="payapp-input-legacy" appearance="outline">
        <input matInput data-testid="payapp-create-name" formControlName="payappName" class="input-field-legacy">
      </mat-form-field>
    </form>
    <div class="generate-button-legacy">
      <app-primary-button data-testid="payapp-create-confirm" (handleClick)="onConfirmClick()">
        Create
      </app-primary-button>
    </div>
  </div>
}

<!-- New Layout - Full dialog with summary -->
@if (!useLegacyLayout) {
  <h1 mat-dialog-title class="dialog-header">
    <span>Generate Pay Application</span>
    <button mat-icon-button (click)="onCancelClick()">
      <mat-icon>close</mat-icon>
    </button>
  </h1>
  <mat-dialog-content>
    <form [formGroup]="payappForm" class="payapp-form">
      <!-- Basic Info -->
      <div class="form-section">
        <label class="payapp-label">Payapp name <span class="required">*</span></label>
        <mat-form-field class="payapp-input" appearance="fill">
          <input matInput data-testid="payapp-create-name" formControlName="payappName" placeholder="e.g., Pay Estimate 16">
          @if (payappForm.get('payappName')?.hasError('required')) {
            <mat-error>
              Payapp name is required
            </mat-error>
          }
        </mat-form-field>
        @if (data.startDate || data.endDate) {
          <div class="period-info">
            <span class="material-symbols-outlined">calendar_month</span>
            <span>Period: {{ getDateRangeMessage() }}</span>
          </div>
        }
      </div>
      <!-- Items Summary -->
      <div class="form-section">
        <h3 class="section-title">Items summary</h3>
        <div class="items-summary">
          <div class="summary-row">
            <span class="label">Ready items:</span>
            <span class="count">{{ data.readyItems?.count || data.itemCount || 0 }}</span>
            <span class="amount">{{ (data.readyItems?.total || data.totalAmount) | currency:'USD' }}</span>
          </div>
          @if (data.deferredItems?.count > 0) {
            <div class="summary-row">
              <span class="label">Deferred items:</span>
              <span class="count">{{ data.deferredItems?.count }}</span>
              <span class="amount">{{ data.deferredItems?.total | currency:'USD' }}</span>
            </div>
          }
          @if (data.rejectedItems?.count > 0) {
            <div class="summary-row">
              <span class="label">Rejected items:</span>
              <span class="count">{{ data.rejectedItems?.count }}</span>
              <span class="amount">{{ data.rejectedItems?.total | currency:'USD' }}</span>
            </div>
          }
        </div>
      </div>
      <!-- Adjustments - Expandable -->
      <div class="form-section entries-section">
        <div class="section-header" (click)="showAdditionalEntries = !showAdditionalEntries">
          <h3 class="section-title">Adjustments</h3>
          <mat-icon class="expand-icon" [class.expanded]="showAdditionalEntries">
            expand_more
          </mat-icon>
        </div>
        @if (showAdditionalEntries) {
          <div class="entries-content">
            <div class="entries-row">
              <!-- Material on Hand -->
              @if (!enableMohPerItem) {
              <div class="entry-item">
                <label class="entry-label">Material on hand</label>
                <mat-form-field class="entry-input" appearance="fill">
                  <span matPrefix class="currency-prefix">$</span>
                  <input matInput type="number" formControlName="material_on_hand" step="0.01" min="0" placeholder="0.00">
                </mat-form-field>
                <span class="entry-hint">Materials stored but not installed</span>
              </div>
              }
              <!-- Retainage Release -->
              <div class="entry-item">
                <label class="entry-label">Retainage release</label>
                <mat-form-field class="entry-input" appearance="fill">
                  <span matPrefix class="currency-prefix">$</span>
                  <input matInput type="number" formControlName="retainage_released" step="0.01" min="0" placeholder="0.00">
                </mat-form-field>
                @if (currentRetainageHeld > 0) {
                  <span class="entry-hint">
                    Held: {{ currentRetainageHeld | currency:'USD' }}
                  </span>
                }
              </div>
            </div>
            <div class="entries-row single">
              <!-- Liquidated Damages -->
              <div class="entry-item ld-item">
                <label class="entry-label">Liquidated damages</label>
                <div class="ld-input-row">
                  <mat-form-field class="days-input" appearance="fill">
                    <input matInput type="number" formControlName="liquidated_damages_days" min="0" placeholder="0">
                    <span matSuffix class="days-suffix">days</span>
                  </mat-form-field>
                  @if (liquidatedDamagesRate > 0 && liquidatedDamagesAmount > 0) {
                    <span class="ld-calc">
                      = {{ liquidatedDamagesAmount | currency:'USD' }}
                    </span>
                  }
                </div>
                @if (liquidatedDamagesRate > 0) {
                  <span class="entry-hint">
                    Rate: {{ liquidatedDamagesRate | currency:'USD' }}/day
                  </span>
                }
              </div>
            </div>
            @if (contractTimeStatus.message) {
              <div class="contract-time-status" [class.warning]="contractTimeStatus.type === 'warning'">
                <span class="material-symbols-outlined">{{ contractTimeStatus.type === 'warning' ? 'warning' : 'info' }}</span>
                <span>{{ contractTimeStatus.message }}</span>
              </div>
            }
          </div>
        }
      </div>
      @if (enableMohPerItem) {
        <div #mohSection class="form-section moh-section" data-testid="moh-create-section">
          @if (validationError) {
            <div class="moh-validation-error" role="alert" data-testid="moh-validation-error">{{ validationError }}</div>
          }
          <div class="section-header moh-section-header" (click)="showMohEntries = !showMohEntries">
            <h3 class="section-title">Materials on hand by bid item</h3>
            <div class="moh-header-summary">
              <span>Stored to date: {{ totalMohClosing | currency:'USD':'symbol':'1.0-0' }}</span>
              <mat-icon class="expand-icon" [class.expanded]="showMohEntries">
                expand_more
              </mat-icon>
            </div>
          </div>
          @if (showMohEntries) {
            <div class="moh-content">
              <div class="moh-search">
                <mat-icon class="moh-search-icon">search</mat-icon>
                <input
                  type="text"
                  class="moh-search-input"
                  placeholder="Search bid items"
                  [formControl]="mohSearchControl"
                  data-testid="moh-create-search"
                />
              </div>
              <div class="moh-search-hint" data-testid="moh-create-search-hint">
                {{ mohSearchHint }}
              </div>
              <div formArrayName="moh" class="moh-lists">
                @if (editedMohRows.length) {
                  <div class="moh-edited" data-testid="moh-create-edited-items">
                    <div class="moh-edited-title">Entries for this pay app</div>
                    <div class="moh-table moh-edited-table">
                      <div class="moh-row moh-header moh-entry-row">
                        <span>Item</span>
                        <span>Stored</span>
                        <span>Advance</span>
                        <span>Drawdown</span>
                        <span>Closing</span>
                        <span></span>
                      </div>
                      @for (entry of editedMohRows; track entry.index) {
                        <div class="moh-row-wrap" [class.has-error]="getMohRowErrors(entry.control).length">
                          <div class="moh-row moh-entry-row" [attr.data-testid]="'moh-edited-row-' + entry.control.get('item_code')?.value">
                            <span class="moh-item">
                              <strong>{{ entry.control.get('item_code')?.value }}</strong>
                              <small>{{ entry.control.get('description')?.value }}</small>
                            </span>
                            <span class="moh-entry-value">{{ entry.control.get('opening_balance')?.value | currency:'USD':'symbol':'1.2-2' }}</span>
                            <span class="moh-entry-value">{{ entry.control.get('advance_added')?.value | currency:'USD':'symbol':'1.2-2' }}</span>
                            <span class="moh-entry-value">{{ entry.control.get('drawdown')?.value | currency:'USD':'symbol':'1.2-2' }}</span>
                            <span class="moh-entry-value">{{ entry.control.get('closing_balance')?.value | currency:'USD':'symbol':'1.2-2' }}</span>
                            <button
                              type="button"
                              mat-icon-button
                              class="moh-clear-button"
                              matTooltip="Clear material entry"
                              (click)="clearMohRow(entry.index)"
                              [attr.data-testid]="'moh-edited-clear-' + entry.control.get('item_code')?.value"
                            >
                              <mat-icon>close</mat-icon>
                            </button>
                          </div>
                          @if (getMohRowErrors(entry.control).length) {
                            <div class="moh-row-errors" role="alert">
                              @for (error of getMohRowErrors(entry.control); track error) {
                                <div class="moh-row-error"><mat-icon svgIcon="error" aria-hidden="true" /><span>{{ error }}</span></div>
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </div>
                }

                <div class="moh-table">
                  <div class="moh-row moh-header">
                    <span>Item</span>
                    <span>Stored</span>
                    <span>Advance</span>
                    <span>Installed</span>
                    <span>Drawdown</span>
                    <span>Closing</span>
                  </div>
                  @for (entry of filteredMohRows; track entry.index) {
                    <div class="moh-row-wrap" [class.has-error]="getMohRowErrors(entry.control).length">
                      <div class="moh-row" [class.has-drawdown-action]="maxMohDrawdown(entry.index) > 0" [formGroupName]="entry.index" [attr.data-testid]="'moh-create-row-' + entry.control.get('item_code')?.value">
                        <span class="moh-item">
                          <strong>{{ entry.control.get('item_code')?.value }}</strong>
                          <small>{{ entry.control.get('description')?.value }}</small>
                        </span>
                        <span>{{ entry.control.get('opening_balance')?.value | currency:'USD':'symbol':'1.2-2' }}</span>
                        <mat-form-field appearance="fill" class="moh-input">
                          <span matPrefix class="currency-prefix">$</span>
                          <input matInput type="number" min="0" step="0.01" formControlName="advance_added" (focus)="clearZeroMohAmount($event)" (blur)="normalizeMohAmount(entry.index, 'advance_added', $event)" (input)="onMohValueChange(entry.index)" [attr.data-testid]="'moh-advance-' + entry.control.get('item_code')?.value">
                        </mat-form-field>
                        <span>{{ entry.control.get('installed_this_period')?.value | currency:'USD':'symbol':'1.2-2' }}</span>
                        <div class="moh-drawdown-cell">
                          <mat-form-field appearance="fill" class="moh-input" [class.moh-field-error]="getMohRowErrors(entry.control).length">
                            <span matPrefix class="currency-prefix">$</span>
                            <input matInput type="number" min="0" step="0.01" formControlName="drawdown" (focus)="clearZeroMohAmount($event)" (blur)="normalizeMohAmount(entry.index, 'drawdown', $event)" (input)="onMohValueChange(entry.index)" [attr.data-testid]="'moh-drawdown-' + entry.control.get('item_code')?.value">
                          </mat-form-field>
                          @if (maxMohDrawdown(entry.index) > 0) {
                            <button type="button" class="moh-use-max"
                              [attr.data-testid]="'moh-use-max-' + entry.control.get('item_code')?.value"
                              [disabled]="entry.control.get('drawdown')?.value >= maxMohDrawdown(entry.index)"
                              [matTooltip]="'Use ' + (maxMohDrawdown(entry.index) | currency:'USD':'symbol':'1.2-2') + ', limited to available materials and installed work'"
                              (click)="useMaxMohDrawdown(entry.index)">Use max</button>
                          }
                        </div>
                        <span>{{ entry.control.get('closing_balance')?.value | currency:'USD':'symbol':'1.2-2' }}</span>
                      </div>
                      @if (getMohRowErrors(entry.control).length) {
                        <div class="moh-row-errors" role="alert">
                          @for (error of getMohRowErrors(entry.control); track error) {
                            <div class="moh-row-error"><mat-icon svgIcon="error" aria-hidden="true" /><span>{{ error }}</span></div>
                          }
                        </div>
                      }
                    </div>
                  }
                  @if (!filteredMohRows.length) {
                    <div class="moh-empty" data-testid="moh-create-empty">
                      {{ hasMohSearch ? 'No bid items found' : 'No items need material on hand review. Search to add an advance for another item.' }}
                    </div>
                  }
                </div>
              </div>
              <div class="moh-totals">
                <span>Advanced: {{ totalMohAdvanced | currency:'USD':'symbol':'1.0-0' }}</span>
                <span>Drawn down: {{ totalMohDrawdown | currency:'USD':'symbol':'1.0-0' }}</span>
                <span>Stored to date: {{ totalMohClosing | currency:'USD':'symbol':'1.0-0' }}</span>
              </div>
            </div>
          }
        </div>
      }
      <!-- Summary -->
      <div class="form-section summary-section">
        <h3 class="section-title">Summary</h3>
        <div class="summary-table">
          <div class="summary-line">
            <span>Work completed this period:</span>
            <span class="value">{{ workCompletedThisPeriod | currency:'USD' }}</span>
          </div>
          @if (!enableMohPerItem) {
          <div class="summary-line">
            <span>+ Material on hand:</span>
            <span class="value">{{ payappForm.get('material_on_hand')?.value | currency:'USD' }}</span>
          </div>
          } @else {
          <div class="summary-line">
            <span>{{ totalMohChange >= 0 ? '+ Net materials stored this period:' : '- Net material drawdown this period:' }}</span>
            <span class="value">{{ totalMohChange | currency:'USD' }}</span>
          </div>
          }
          <div class="summary-line subtotal">
            <span>= Total earnings:</span>
            <span class="value">{{ totalEarnings | currency:'USD' }}</span>
          </div>
          <div class="summary-line deduction">
            <span>- Retainage ({{ retainagePercentage }}%):</span>
            <span class="value">{{ retainageAmount | currency:'USD' }}</span>
          </div>
          <div class="summary-line addition">
            <span>+ Retainage released:</span>
            <span class="value">{{ retainageReleased | currency:'USD' }}</span>
          </div>
          <div class="summary-line deduction">
            <span>- Liquidated damages:</span>
            <span class="value">{{ liquidatedDamagesAmount | currency:'USD' }}</span>
          </div>
          <div class="summary-line net-deductions">
            <span>= Net deductions:</span>
            <span class="value">{{ netDeductions | currency:'USD' }}</span>
          </div>
          <div class="summary-line total">
            <span>Amount due this period:</span>
            <span class="value">{{ amountDueThisPeriod | currency:'USD' }}</span>
          </div>
        </div>
      </div>
    </form>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button data-testid="payapp-create-cancel" mat-stroked-button (click)="onCancelClick()">Cancel</button>
    <button data-testid="payapp-create-confirm" mat-flat-button color="primary" (click)="onConfirmClick()" [disabled]="validating">
      {{ validating ? 'Validating...' : 'Create payapp' }}
    </button>
  </mat-dialog-actions>
}
